// src/components/agent/DeliveryForm.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ChevronLeft } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { agentsAPI, deliveriesAPI } from '../../services/api';
import { getDisplayAddress, getPropertyBillPrimary, getPropertyBillSecondary } from '../../utils/displayName';

function formatLoginPhone(phone) {
	if (phone == null || String(phone).trim() === '') return '';
	const t = String(phone).trim();
	return t.startsWith('+') ? t : `+${t}`;
}

const DeliveryForm = () => {
	const { user } = useAuth();
	const agentId = user?.id ?? user?.userId;
	const navigate = useNavigate();
	const { billNo } = useParams();
	const [property, setProperty] = useState(null);
	const [loading, setLoading] = useState(true);
	const [submitting, setSubmitting] = useState(false);
	const [recipientName, setRecipientName] = useState('');
	const [recipientPhone, setRecipientPhone] = useState('');
	const [deliveryLocation, setDeliveryLocation] = useState(null);

	useEffect(() => {
		const fetchProperty = async () => {
			try {
				setLoading(true);
				if (!agentId || !billNo) {
					toast.error('Missing required information');
					navigate('/agent/deliver-bills');
					return;
				}
				const data = await agentsAPI.getBillDetails(agentId, billNo);
				setProperty(data);
				// Pre-fill recipient name with owner name if available
				if (data.ownerName) {
					setRecipientName(data.ownerName);
				}
				// Recipient phone = agent login number (same as OTP / password login)
				const loginPhone = formatLoginPhone(user?.phone ?? user?.user?.phone);
				if (loginPhone) {
					setRecipientPhone(loginPhone);
				}
			} catch (error) {
				toast.error('Failed to load property details');
				console.error('Error fetching property:', error);
				navigate('/agent/deliver-bills');
			} finally {
				setLoading(false);
			}
		};

		if (user && billNo) {
			fetchProperty();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user, billNo]);

	useEffect(() => {
		if (!user || !navigator.geolocation) return;
		navigator.geolocation.getCurrentPosition(
			(pos) => {
				setDeliveryLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
			},
			() => {
				setDeliveryLocation(null);
			},
			{ enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
		);
	}, [user]);

	const handleSubmit = async () => {
		if (!recipientName || !recipientPhone) {
			toast.error('Please fill all fields');
			return;
		}

		if (!property) {
			toast.error('Property data not loaded');
			return;
		}

		if (!agentId) {
			toast.error('Agent not authenticated');
			return;
		}

		try {
			setSubmitting(true);
			
			// Use property ID from fetched property data
			if (!property.id) {
				toast.error('Property ID not found');
				return;
			}
			
			// Create delivery record (including optional delivery location)
			await deliveriesAPI.create({
				propertyId: property.id,
				recipientName,
				recipientPhone,
				deliveryMethod: 'Physical Delivery',
				agentLat: deliveryLocation?.lat,
				agentLng: deliveryLocation?.lng,
			});

			toast.success('Bill delivery recorded successfully!');
			navigate('/agent/deliver-bills');
		} catch (error) {
			toast.error(error.response?.data?.message || 'Failed to record delivery');
			console.error('Error submitting delivery:', error);
		} finally {
			setSubmitting(false);
		}
	};

	if (loading) {
		return (
			<div className='h-full flex items-center justify-center'>
				<p className='text-white/60'>Loading...</p>
			</div>
		);
	}

	if (!property) {
		return (
			<div className='h-full flex items-center justify-center'>
				<p className='text-white/60'>Property not found</p>
			</div>
		);
	}

	return (
		<div className='h-full flex flex-col'>
			<header className='p-6 bg-[#001E3C] flex items-center gap-4 border-b border-white/10'>
				<button
					type='button'
					onClick={(e) => {
						e.preventDefault();
						navigate('/agent/deliver-bills');
					}}
					className='glass-card p-3 rounded-xl'>
					<ChevronLeft className='w-5 h-5 text-white' />
				</button>
				<h2 className='text-xl font-black uppercase text-white'>Deliver Bill</h2>
			</header>

			<div className='flex-1 p-6 space-y-6 overflow-y-auto no-scrollbar'>
				<div className='glass-card p-6'>
					<p className='font-mono font-bold text-lg mb-1 text-white'>
						{getPropertyBillPrimary(property)}
					</p>
					{getPropertyBillSecondary(property) && (
						<p className='text-xs text-white/50 font-mono mb-1'>
							Valuation: {getPropertyBillSecondary(property)}
						</p>
					)}
					<p className='text-lg font-bold text-white'>
						{getDisplayAddress(property)}
					</p>
					<p className='text-2xl font-bold text-[#FFC107] mt-2'>
					₵{(property.totalDue || property.billAmount || 0).toLocaleString()}
					</p>
				</div>

				<div className='space-y-4'>
					<div>
						<label className='block text-sm font-bold mb-2 text-white'>
							Recipient Name
						</label>
						<input
							type='text'
							value={recipientName}
							onChange={(e) => setRecipientName(e.target.value)}
							className='w-full p-4 bg-white/5 border border-white/10 rounded-xl outline-none text-white placeholder-white/30'
							placeholder='Enter recipient name'
						/>
					</div>
					<div>
						<label className='block text-sm font-bold mb-2 text-white'>
							Recipient Phone Number
						</label>
						<input
							type='tel'
							value={recipientPhone}
							readOnly={Boolean(formatLoginPhone(user?.phone ?? user?.user?.phone))}
							onChange={(e) => setRecipientPhone(e.target.value)}
							className={`w-full p-4 border border-white/10 rounded-xl outline-none text-white placeholder-white/30 ${
								formatLoginPhone(user?.phone ?? user?.user?.phone)
									? 'bg-white/[0.07] cursor-default'
									: 'bg-white/5'
							}`}
							placeholder='+233XXXXXXXXX'
							aria-describedby='delivery-recipient-phone-hint'
						/>
						<p id='delivery-recipient-phone-hint' className='mt-1.5 text-xs text-white/50'>
							{formatLoginPhone(user?.phone ?? user?.user?.phone)
								? 'Uses your registered login number for this delivery record.'
								: 'Add your phone if it is missing from your profile.'}
						</p>
					</div>
				</div>

				<button
					type='button'
					onClick={handleSubmit}
					disabled={submitting}
					className='w-full py-4 rounded-2xl btn-gold font-black uppercase text-sm mt-8 disabled:opacity-50 disabled:cursor-not-allowed'>
					{submitting ? 'Submitting...' : 'Deliver Bill'}
				</button>
			</div>
		</div>
	);
};

export default DeliveryForm;
